import { Suspense, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Await, Link, useFetchers, useMatches } from "react-router";
import { Pagination } from "swiper/modules";
import { useSwiper } from "swiper/react";

import { UsersIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { ShoppingBagIcon } from "@heroicons/react/24/outline";
import {
  CaretLeftIcon,
  CaretRightIcon,
  CheckIcon,
  StarFilledIcon,
  StarIcon,
} from "@radix-ui/react-icons";

import { isValidationError } from "~/components/forms/validationErrorResponse";
import { ProductImage } from "~/components/product-list/product-image";
import { Button } from "~/components/ui/button";
import { Carousel } from "~/components/ui/carousel";
import { Label } from "~/components/ui/label";
import { P } from "~/components/ui/text";
import { TriButton } from "~/components/ui/tri-button";
import { useURL } from "~/contexts";
import * as gtag from "~/google-analytics/utils/gtags.client";
import type {
  ProductHitWithSwatches,
  ProductSearchHit,
  ProductSwatch,
} from "~/lib/app.types";
import { LOW_STOCK_NUM } from "~/lib/constants";
import type { SerializeFrom } from "~/lib/types";
import { cn } from "~/lib/ui";
import { invariant } from "~/lib/utils/utils";
import { useFormatPrice } from "~/shop/utils/price";

import {
  ProductQuickView,
  ProductQuickViewDialog,
} from "../../../components/products/product-quick-view";
import * as ProductSwatches from "../../product-list/components/product-swatch";
import {
  useProductTileData,
  useProductTileQuickView,
} from "../../product-list/components/product-tile";
import type { action } from "../basket";
import { useBasket, useBasketActions } from "../basket";

const BIG_ICON_SIZE = 30;
const MID_ICON_SIZE = 20;

export const ADD_TO_CART_RECOMMENDATIONS_KEY = "addToCardRecommendations";

const isRecommendationData = (data: unknown): data is ProductSearchHit[] => {
  if (!Array.isArray(data)) return false;
  return data.some(item => "productId" in item);
};

export const createRecommendationData = <T extends ProductSearchHit>(
  productSearchHits: () => Promise<T[]>,
) => {
  return { addToCardRecommendations: productSearchHits() };
};

export function CarouselControls() {
  const swiper = useSwiper();
  const [hiddenLeft, setHiddenLeft] = useState<boolean>(swiper.isBeginning);
  const [hiddenRight, setHiddenRight] = useState<boolean>(swiper.isEnd);
  const onControlClick = (isLeft?: boolean) => {
    if (isLeft) {
      swiper.slidePrev();
    } else {
      swiper.slideNext();
    }
    setHiddenLeft(swiper.isBeginning);
    setHiddenRight(swiper.isEnd);
  };
  return (
    <div className="absolute right-3 top-3 z-10">
      <div className="flex flex-row gap-3">
        <Button
          variant="outline"
          className="h-6 w-6 rounded-full bg-page-background p-0 text-base text-text-dark-on-light shadow-md disabled:border-0"
          onClick={() => onControlClick(true)}
          disabled={hiddenLeft}
        >
          <CaretLeftIcon
            width={MID_ICON_SIZE}
            height={MID_ICON_SIZE}
            className="h-3 w-3 origin-center object-none"
          />
        </Button>
        <Button
          variant="outline"
          className="h-6 w-6 rounded-full bg-page-background p-0 text-base text-text-dark-on-light shadow-md disabled:border-0"
          onClick={() => onControlClick()}
          disabled={hiddenRight}
        >
          <CaretRightIcon
            width={MID_ICON_SIZE}
            height={MID_ICON_SIZE}
            className="z-20origin-center top-0 h-3 w-3 object-none"
          />
        </Button>
      </div>
    </div>
  );
}

export const RecommendationProductTile = ({
  className,
  product,
  quickNav,
  totalProducts,
  closeFlyout,
  setQuickViewData,
  ref,
}: React.HTMLAttributes<HTMLDivElement> & {
  key: string;
  product: ProductHitWithSwatches;
  quickNav?: ProductHitWithSwatches[];
  totalProducts?: number;
  closeFlyout?: () => void;
  setQuickViewData: (data: {
    product: ProductHitWithSwatches;
    swatch: ProductSwatch;
  }) => void;
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const basket = useBasketActions();
  const formatPrice = useFormatPrice();
  const [isHovered, setIsHovered] = useState(false);
  const {
    getHistoryState,
    getVariantURL,

    defaultSwatch,
    swatches,
    url,
  } = useProductTileData(product, quickNav, totalProducts);

  const addGtagEvent = () => {
    /* For GA4 */
    gtag.event({
      action: "add_to_bag_clicked",
      category: "recommendation_product_tile",
      label: "added_product_id",
      value: product?.productId,
    });
  };
  const salePrice = product.c_saleprice;
  const standardPrice = product?.c_pricing?.standardprice;

  return (
    <ProductSwatches.SwatchesProvider
      swatches={swatches}
      ref={ref}
      key={product.productId}
      className={cn("flex w-[231px] items-start gap-2", className)}
      defaultValue={defaultSwatch}
    >
      {swatch => (
        <>
          <div className="flex h-s-product-card-height w-20 shrink-0 flex-col items-start pt-1.5">
            <Link
              to={getVariantURL(swatch.variationGroup)}
              onMouseMove={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              state={getHistoryState(
                swatch,
                swatch?.images.large?.images[isHovered ? 1 : 0] ||
                  product.image,
              )}
              onClick={closeFlyout}
            >
              <ProductImage
                image={
                  swatch?.images.large?.images[isHovered ? 1 : 0] ||
                  product.image
                }
                type="plp"
                className="h-s-product-card-height w-[80px] rounded-xl"
                small
              />
            </Link>
          </div>
          <div className="flex flex-1-0-0 flex-col items-start justify-center gap-0.5 px-0 pb-5 pt-1.5">
            <Link
              to={url(
                `/${
                  swatch?.variationGroup?.productId || product.productId
                }.html`,
              )}
              state={getHistoryState(
                swatch,
                swatch?.images.large?.images[isHovered ? 1 : 0] ||
                  product.image,
              )}
              onClick={closeFlyout}
            >
              <p className="flex w-full items-start justify-start pt-1 text-left text-sm font-semibold">
                {product.productName}
              </p>
            </Link>
            <div className="flex content-start items-start gap-1 self-stretch">
              <p className="py-1 text-xs font-light tracking-1 text-text-dark-on-light">
                {standardPrice ? (
                  <>
                    <span className="line-through">
                      {formatPrice(standardPrice)}
                    </span>
                    <span className="pl-1 text-salePrice">
                      {formatPrice(product?.c_pricing?.salesprice)}
                    </span>
                  </>
                ) : (
                  <>
                    {!salePrice &&
                      formatPrice(
                        swatch?.variationGroup?.price || product.price,
                      )}
                    {salePrice && (
                      <>
                        <span className="line-through">
                          {formatPrice(
                            swatch?.variationGroup?.price || product.price,
                          )}
                        </span>
                        <span className="pl-1 text-salePrice">
                          {formatPrice(salePrice)}
                        </span>
                      </>
                    )}
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center">
              {[0, 1, 2, 3, 4].map(rating =>
                Math.round(
                  parseFloat(product?.c_productInfo?.c_bvAverageRating || "0"),
                ) > rating ? (
                  <StarFilledIcon
                    key={rating}
                    className={cn(
                      "h-3 w-3 flex-shrink-0 text-text-dark-on-light",
                    )}
                    aria-hidden="true"
                  />
                ) : (
                  <StarIcon
                    key={rating}
                    className={cn(
                      "h-3 w-3 flex-shrink-0 text-text-dark-on-light",
                    )}
                    aria-hidden="true"
                  />
                ),
              )}
            </div>
            {swatches.length > 0 && (
              <ProductSwatches.SwatchList
                max={5}
                className="flex flex-row items-start justify-start self-stretch pt-1"
                swatchClass="h-5 w-5 p-0.5"
              />
            )}
            {product.c_productInfo?.inventory?.stockLevel ||
              (0 <= LOW_STOCK_NUM && (
                <div className="flex flex-col items-start self-stretch pt-1.5">
                  <div className="flex w-fit items-center gap-1 self-stretch rounded-full bg-accentalpha-7 px-2.5 py-2">
                    <UsersIcon
                      width={16}
                      height={16}
                      className="text-icons-light-on-dark"
                    />
                    <p className="flex-1-0-0 text-left text-xs font-normal leading-1 text-contrast-white">
                      Hurry! only {product.c_productInfo?.inventory?.stockLevel}{" "}
                      left
                    </p>
                  </div>
                </div>
              ))}
            <div className="flex flex-col items-start self-stretch bg-inherit pt-2.5">
              <TriButton
                variant="outline"
                className={cn(
                  "flex items-center justify-center gap-2 rounded-small-4-max border-neutral-4 px-3 py-0",
                )}
                form="buyForm"
                isLoading={basket.fetcher.state !== "idle"}
                onClick={() => {
                  addGtagEvent();
                  setQuickViewData({ product, swatch });
                  closeFlyout?.();
                }}
              >
                Add to Bag
              </TriButton>
            </div>
          </div>
        </>
      )}
    </ProductSwatches.SwatchesProvider>
  );
};
RecommendationProductTile.displayName = "RecommendationProductTile";

export default function AddedToBasketFlyout() {
  const formatPrice = useFormatPrice();
  const basket = useBasketActions();
  const fetchers = useFetchers();
  const url = useURL();
  const {
    quickViewSwatch,
    quickViewProduct,
    quickViewFetcherData,
    setQuickViewData,
    onQuickViewClose,
  } = useProductTileQuickView({
    fetcherData: basket.fetcher.data,
  });
  const { openBasket } = useBasket() ?? {};
  const recommendations = useMatches()
    .map(match => {
      if (
        match.data &&
        typeof match.data === "object" &&
        ADD_TO_CART_RECOMMENDATIONS_KEY in match.data
      )
        return match.data[ADD_TO_CART_RECOMMENDATIONS_KEY];
    })
    .find(e => e !== undefined);
  useEffect(() => {
    fetchers.map(fetcher => {
      const active =
        fetcher.formAction?.includes("/resources/basket") &&
        fetcher.state === "loading" &&
        fetcher.data &&
        fetcher.data.successfullyAdded;

      const data = fetcher.data as SerializeFrom<typeof action>;
      if (!data || !("successfullyAdded" in data)) return;
      if (isValidationError(data)) {
        toast.error("An error occurred while adding the product to the bag");
        console.error("Error response", data);
        return;
      }
      /* Narrow the type here for the add to bag action */
      invariant("successfullyAdded" in data, "Invalid data");
      const product = active && data.product;
      const quantity = active && fetcher.data.quantity;
      const salesPrice = product?.productData?.c_salesprice;
      const standardPrice = product?.productData?.c_pricing?.standardprice;

      if (active) {
        toast.custom(
          (t: { id: string | undefined }) => (
            <>
              <div
                className={cn(
                  "w-[312px] rounded-lg border border-neutralalpha-6 bg-page-background px-0 py-3 shadow-md",
                  !open && "hidden",
                )}
              >
                <div className="px-3 py-0 text-left">
                  <CheckIcon
                    className="my-2 inline-block text-success-10"
                    height={BIG_ICON_SIZE}
                    width={BIG_ICON_SIZE}
                  />
                  <Label
                    className="text-xl font-semibold tracking-5"
                    data-testid="add-to-basket-flyout-title"
                  >
                    Added to Bag{" "}
                  </Label>
                  <div className="float-right flex h-6 w-6 flex-1-0-0 cursor-pointer items-center justify-center self-stretch rounded-full text-right shadow-md">
                    <XMarkIcon
                      className="text-icons-dark-on-light"
                      onClick={() => toast.remove(t.id)}
                      width={MID_ICON_SIZE}
                      height={MID_ICON_SIZE}
                    />
                  </div>
                  <div className="flex items-start gap-2 self-stretch">
                    {product.image && (
                      <ProductImage
                        image={product.image[0]}
                        type="plp"
                        className="h-s-product-card-height w-20 rounded-small-2"
                        small
                      />
                    )}
                    <div className="flex flex-1-0-0 flex-col items-start justify-center gap-0.5 px-0 pb-2 pt-1.5">
                      <p className="text-sm font-semibold leading-2">
                        {product.name}
                      </p>
                      <p>
                        {standardPrice ? (
                          <>
                            <span className="line-through">
                              {formatPrice(quantity * standardPrice)}
                            </span>
                            <span className="pl-1 text-salePrice">
                              {formatPrice(
                                quantity *
                                  product?.productData?.c_pricing?.salesprice,
                              )}
                            </span>
                          </>
                        ) : (
                          <>
                            {!salesPrice &&
                              formatPrice(
                                quantity * product?.productData.price,
                              )}
                            {salesPrice && (
                              <>
                                <span className="line-through">
                                  {formatPrice(
                                    quantity * product?.productData.price,
                                  )}
                                </span>
                                <span className="pl-1 text-salePrice">
                                  {formatPrice(quantity * salesPrice)}
                                </span>
                              </>
                            )}
                          </>
                        )}
                      </p>

                      <p className="text-xs font-light">Qty: {quantity}</p>
                    </div>
                  </div>
                </div>
                <div className="my-2 flex flex-row gap-4 p-3">
                  <Button
                    variant="default"
                    className="flex-rol flex h-6 w-full flex-1-0-0 items-center gap-2 text-sm text-neutral-1"
                    onClick={() => {
                      openBasket?.();
                      toast.remove(t.id);
                    }}
                  >
                    <ShoppingBagIcon
                      width={16}
                      height={16}
                      className="inline text-inherit"
                    />
                    View Bag
                  </Button>
                  <Link
                    to={url("/checkout")}
                    className="w-1/2 hover:no-underline"
                  >
                    <Button
                      variant="outline"
                      className="flex-rol flex h-6 w-full flex-1-0-0 items-center gap-2 text-sm text-neutral-11"
                      onClick={() => toast.remove(t.id)}
                    >
                      Checkout
                      <CaretRightIcon
                        width={16}
                        height={16}
                        className="inline text-inherit"
                      />
                    </Button>
                  </Link>
                </div>
                <div className="relative mt-4 flex flex-col border-t border-gray-400 px-3 pt-3">
                  <Suspense>
                    <Await resolve={recommendations}>
                      {recommendations => {
                        return (
                          isRecommendationData(recommendations) && (
                            <>
                              <P className="py-3 text-xl font-semibold tracking-5">
                                You may also like
                              </P>
                              <Carousel
                                slideClassName="bg-inherit"
                                className="!static h-auto w-[231px] bg-inherit [&_div]:!bottom-0"
                                customNavigation={<CarouselControls />}
                                pagination={{
                                  bulletClass: "swiper-pagination-bullet",
                                  clickable: true,
                                }}
                                modules={[Pagination]}
                                slidesPerView={1.1}
                              >
                                {recommendations.map(recommendation => (
                                  <RecommendationProductTile
                                    key={recommendation.productName || ""}
                                    product={recommendation}
                                    closeFlyout={() => toast.remove(t.id)}
                                    className="w-full pr-3"
                                    setQuickViewData={setQuickViewData}
                                  />
                                ))}
                              </Carousel>
                            </>
                          )
                        );
                      }}
                    </Await>
                  </Suspense>
                </div>
              </div>
            </>
          ),
          {
            position: "top-right",
            id: "added-to-bag-toast", // adding an id prevents duplication of this specific toast
          },
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchers]);

  return (
    <ProductQuickViewDialog
      open={!!quickViewProduct}
      onClose={onQuickViewClose}
    >
      {quickViewProduct && quickViewSwatch && (
        <ProductQuickView
          product={quickViewProduct}
          fetcherData={quickViewFetcherData}
          currentSwatch={quickViewSwatch}
          onClose={onQuickViewClose}
        />
      )}
    </ProductQuickViewDialog>
  );
}
